import type { Plan, PlanAccent, PlanFeature, TokenPackage } from "@/pages/Pricing/types";

// Re-export types for convenience
export type { Plan, PlanAccent, PlanFeature, TokenPackage };

export type PlanId = "free" | "starter" | "pro" | "creator" | "debug";

/** Default plan for users without a subscription */
export const DEFAULT_PLAN_ID: PlanId = "free";

/** Highest-tier plan — used to determine "Manage Plan" vs "Upgrade Plan" */
export const HIGHEST_PLAN_ID: PlanId = "creator";

/** Debug plan id — ₪1/month recurring, visible/usable only by admins (profile.role === 'admin').
 *  Intentionally NOT added to `PLANS` so it never appears on the public Pricing page.
 *  The server enforces the boundary via the same `profiles.role` check; this constant is the UI id. */
export const DEBUG_PLAN_ID: PlanId = "debug";

// ---------------------------------------------------------------------------
// Annual billing discount (20% off)
// ---------------------------------------------------------------------------
export const ANNUAL_DISCOUNT = 0.80;

// Margin analysis and API costs → see src/config/pricing.ts

// ---------------------------------------------------------------------------
// Plan definitions — display prices (server `server/src/config/payplus.ts` is
// the canonical source for actual charges; keep NIS values in sync)
// ---------------------------------------------------------------------------
export const PLANS: Plan[] = [
  {
    id: "free",
    name: "FREE",
    monthlyPrice: 0,
    tokens: 100,
    cta: { he: "התחל בחינם", en: "Start Free" },
    popular: false,
    accent: {
      text: "text-gray-400",
      bgSubtle: "bg-gray-400/10",
      borderSubtle: "border-gray-400/20",
      ctaBg: "bg-gray-600 hover:bg-gray-500",
      ctaText: "text-white",
      topBorder: "border-t-gray-400/60",
    },
  },
  {
    id: "starter",
    name: "STARTER",
    monthlyPrice: 10,
    monthlyPriceNIS: 35,
    tokens: 500,
    cta: { he: "בחר Starter", en: "Choose Starter" },
    popular: true,
    accent: {
      text: "text-violet-400",
      bgSubtle: "bg-violet-400/10",
      borderSubtle: "border-violet-400/20",
      ctaBg: "bg-violet-500 hover:bg-violet-400",
      ctaText: "text-white",
      topBorder: "border-t-violet-400",
    },
  },
  {
    id: "pro",
    name: "PRO",
    monthlyPrice: 27,
    monthlyPriceNIS: 95,
    tokens: 1500,
    cta: { he: "בחר PRO", en: "Choose PRO" },
    popular: false,
    badge: { he: "הפופולרי ביותר", en: "Most Popular" },
    accent: {
      text: "text-brand",
      titleText: "text-transparent bg-clip-text gradient-primary",
      bgSubtle: "bg-brand/10",
      borderSubtle: "border-brand/20",
      ctaBg: "gradient-primary hover:opacity-90",
      ctaText: "text-white",
      topBorder: "border-t-brand",
    },
  },
  {
    id: "creator",
    name: "CREATOR",
    monthlyPrice: 55,
    monthlyPriceNIS: 190,
    tokens: 3200,
    cta: { he: "בחר CREATOR", en: "Choose CREATOR" },
    popular: false,
    badge: { he: "המשתלם ביותר", en: "Best Value" },
    accent: {
      text: "text-amber-400",
      bgSubtle: "bg-amber-400/10",
      borderSubtle: "border-amber-400/20",
      ctaBg: "bg-amber-500 hover:bg-amber-400",
      ctaText: "text-gray-900",
      topBorder: "border-t-amber-400",
    },
  },
];

// ---------------------------------------------------------------------------
// Derived look-up maps
// ---------------------------------------------------------------------------
export const PLAN_CREDITS: Record<PlanId, number> = Object.fromEntries(
  PLANS.map((p) => [p.id, p.tokens]),
) as Record<PlanId, number>;

/** Get the NIS price for a plan + billing period (for proration display). */
export function getPlanPriceNIS(planId: PlanId, billingPeriod: "monthly" | "annual"): number {
  const plan = PLANS.find((p) => p.id === planId);
  if (!plan || !plan.monthlyPriceNIS) return 0;
  return billingPeriod === "annual"
    ? Math.ceil(plan.monthlyPriceNIS * 12 * ANNUAL_DISCOUNT)
    : plan.monthlyPriceNIS;
}

/** Get the display price for a plan in the correct currency based on language. */
export function getPlanPrice(planId: PlanId, billingPeriod: "monthly" | "annual", language: "he" | "en"): number {
  const plan = PLANS.find((p) => p.id === planId);
  if (!plan) return 0;

  const base = language === "he"
    ? (plan.monthlyPriceNIS ?? plan.monthlyPrice)
    : plan.monthlyPrice;

  return billingPeriod === "annual"
    ? Math.ceil(base * 12 * ANNUAL_DISCOUNT)
    : base;
}

// ---------------------------------------------------------------------------
// Initial credits for non-logged-in / new users (matches free plan)
// ---------------------------------------------------------------------------
const freePlan = PLANS.find((p) => p.id === "free")!;
export const INITIAL_CREDITS = freePlan.tokens;
export const INITIAL_MAX_CREDITS = freePlan.tokens;

// ---------------------------------------------------------------------------
// Token packages for purchase (prices in NIS)
// ---------------------------------------------------------------------------
export const TOKEN_PACKAGES: TokenPackage[] = [
  { tokens: 100, priceNIS: 10, priceUSD: 3, originalPriceNIS: 13, originalPriceUSD: 4 },
  { tokens: 200, priceNIS: 19, priceUSD: 5, originalPriceNIS: 24, originalPriceUSD: 6 },
  { tokens: 300, priceNIS: 25, priceUSD: 7, originalPriceNIS: 31, originalPriceUSD: 9 },
  { tokens: 600, priceNIS: 49, priceUSD: 14, originalPriceNIS: 61, originalPriceUSD: 18 },
  { tokens: 1200, priceNIS: 95, priceUSD: 26, originalPriceNIS: 119, originalPriceUSD: 33 },
  { tokens: 2400, priceNIS: 179, priceUSD: 49, originalPriceNIS: 224, originalPriceUSD: 61 },
];

// ---------------------------------------------------------------------------
// Feature lists for pricing display
// ---------------------------------------------------------------------------
export const SHARED_FEATURES: PlanFeature[] = [
  { text: { he: "איכות עד 4K", en: "Up to 4K quality" } },
];

export const PLAN_SPECIFIC_FEATURES: Record<PlanId, PlanFeature[]> = {
  // Dev-only debug plan — no feature list (never rendered on Pricing).
  debug: [],
  creator: [
    { text: { he: "התוכן שלך פרטי", en: "Your content is private" }, included: true },
    { text: { he: "יצירה במקביל", en: "Parallel generation" }, included: true },
    { text: { he: "עריכה מתקדמת (Full Editor)", en: "Advanced editing (Full Editor)" }, included: true },
    { text: { he: "יצירת וידאו", en: "Video creation" }, included: true },
    { text: { he: "ללא סימן מים", en: "No watermark" }, included: true },
    { text: { he: "פרומטים מותאמים אישית", en: "Custom style prompts" }, included: true },
  ],
  pro: [
    { text: { he: "התוכן שלך פרטי", en: "Your content is private" }, included: true },
    { text: { he: "יצירה במקביל", en: "Parallel generation" }, included: true },
    { text: { he: "עריכה מתקדמת (Full Editor)", en: "Advanced editing (Full Editor)" }, included: true },
    { text: { he: "יצירת וידאו", en: "Video creation" }, included: true },
    { text: { he: "ללא סימן מים", en: "No watermark" }, included: true },
    { text: { he: "פרומטים מותאמים אישית", en: "Custom style prompts" }, included: true },
  ],
  starter: [
    { text: { he: "התוכן שלך פרטי", en: "Your content is private" }, included: true },
    { text: { he: "יצירה במקביל", en: "Parallel generation" }, included: false },
    { text: { he: "עריכה מתקדמת (Full Editor)", en: "Advanced editing (Full Editor)" }, included: true },
    { text: { he: "יצירת וידאו", en: "Video creation" }, included: true },
    { text: { he: "ללא סימן מים", en: "No watermark" }, included: true },
    { text: { he: "פרומטים מותאמים אישית", en: "Custom style prompts" }, included: true },
  ],
  free: [
    { text: { he: "התוכן שלך ציבורי", en: "Your content is public" }, included: false },
    { text: { he: "יצירה במקביל", en: "Parallel generation" }, included: false },
    { text: { he: "עריכה מתקדמת (Full Editor)", en: "Advanced editing (Full Editor)" }, included: true },
    { text: { he: "יצירת וידאו", en: "Video creation" }, included: true },
    { text: { he: "ללא סימן מים", en: "No watermark" }, included: true },
    { text: { he: "פרומטים מותאמים אישית", en: "Custom style prompts" }, included: true },
  ],
};
